import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { useAuth } from '../AuthContext.tsx';
import { Icon } from './FdPrimitives.tsx';

type Theme = 'light' | 'dark';

type NavItem = {
  to: string;
  label: string;
  icon: string;
  end?: boolean;
};

const navItems: NavItem[] = [
  { to: '/', label: 'KPIs', icon: 'monitoring', end: true },
  { to: '/devices', label: 'Devices', icon: 'devices' },
  { to: '/sessions', label: 'Sessions', icon: 'timeline' },
  { to: '/decisions', label: 'Decisions', icon: 'gavel' },
  { to: '/rules', label: 'Rules', icon: 'rule' },
  { to: '/webhooks', label: 'Webhooks', icon: 'webhook' },
];

const crumbByPath: Record<string, string> = {
  '/': 'KPIs',
  '/devices': 'Devices',
  '/sessions': 'Sessions',
  '/decisions': 'Decisions',
  '/rules': 'Rules',
  '/webhooks': 'Webhooks',
};

export function Layout() {
  const { tenant, logout } = useAuth();
  const { pathname } = useLocation();
  const [theme, setTheme] = useState<Theme>(readInitialTheme);

  useEffect(() => {
    const storage = getStorage();
    if (storage) {
      storage.setItem('fdtheme', theme);
    }
  }, [theme]);

  const crumb = useMemo(() => pageCrumb(pathname), [pathname]);
  const tenantInitials = (tenant?.tenant_id ?? 'FD').slice(0, 2).toUpperCase();
  const isDark = theme === 'dark';
  const toggleLabel = isDark ? 'Switch to light mode' : 'Switch to dark mode';

  return (
    <div className={isDark ? 'fd theme-dark' : 'fd'} id="fd">
      <div className="shell">
        <aside className="side">
          <div className="sbrand">
            <img src={`${import.meta.env.BASE_URL}fd-logo-mark-light.png`} alt="FD" />
            <span className="bn">FDID Console</span>
          </div>
          <nav className="nav2" aria-label="Console navigation">
            {renderNavItems(navItems.slice(0, 1))}
            <div className="navseg">Explore</div>
            {renderNavItems(navItems.slice(1, 4))}
            <div className="navseg">Configure</div>
            {renderNavItems(navItems.slice(4))}
          </nav>
        </aside>
        <div className="main">
          <div className="topbar">
            <span className="crumb">{crumb}</span>
            <span className="tr">
              <button
                aria-label={toggleLabel}
                className={isDark ? 'tgl theme-toggle is-dark' : 'tgl theme-toggle'}
                onClick={() => setTheme(isDark ? 'light' : 'dark')}
                type="button"
              >
                <Icon name="light_mode" />
                <span className="sw">
                  <i>
                    <Icon name={isDark ? 'dark_mode' : 'light_mode'} />
                  </i>
                </span>
                <Icon name="dark_mode" />
              </button>
              <span className="av" title={tenant?.tenant_id ?? 'Unknown tenant'}>{tenantInitials}</span>
              <button className="btn sec sm" type="button" onClick={logout}>
                Logout
              </button>
            </span>
          </div>
          <main className="content">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}

function renderNavItems(items: NavItem[]) {
  return items.map((item) => (
    <NavLink
      className={({ isActive }) => (isActive ? 'ni on' : 'ni')}
      end={item.end}
      key={item.to}
      to={item.to}
    >
      <Icon name={item.icon} />
      {item.label}
    </NavLink>
  ));
}

function readInitialTheme(): Theme {
  return getStorage()?.getItem('fdtheme') === 'dark' ? 'dark' : 'light';
}

function pageCrumb(pathname: string): string {
  if (crumbByPath[pathname]) return crumbByPath[pathname];
  if (pathname.startsWith('/devices/')) return 'Devices / Detail';
  if (pathname.startsWith('/sessions/')) return 'Sessions / Detail';
  return 'Console';
}

function getStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage;
}
